import type { ConditionField, ReploState } from "schemas/generated/symbol";
import type { PredefinedVariant, PredefinedVariantType } from "./types";

import { v4 as uuidv4 } from "uuid";

export function findDefault(variants: ReploState[]) {
  return variants.find(isDefaultVariant)!;
}

export function isDefaultVariant(
  variant: ReploState,
): variant is ReploState & { name: "default" } {
  return variant.name === "default";
}

export function findVariant(variants: ReploState[], id: string) {
  return variants.find((variant) => variant.id === id) ?? null;
}

export function findVariantByConditionField(
  variants: ReploState[],
  conditionField: string,
) {
  return variants.find((variant) => {
    return variant.query?.statements.some(
      (statement) => statement.field === conditionField,
    );
  });
}

export const isHoverVariant = (variant: ReploState) => {
  return variant.query?.statements.find(
    (item) => item.field === "interaction.hover",
  );
};

export const isHashmarkVariant = (variant: ReploState) => {
  return variant.query?.statements.find(
    (item) => item.field === "page.hashmark",
  );
};

type ReploVariantWithState = ReploState & {
  isDefault: boolean;
  isActive: boolean;
};

/**
 * Augments variants with information about their state
 */
export function getVariantsWithState(
  variants: ReploState[],
  activeVariantId?: string | null,
) {
  const defaultVariantId = findDefault(variants)?.id;

  return variants.map((variant) => {
    // Legacy cast - may or may not be necessary
    // eslint-disable-next-line replo/no-as-cast
    return {
      ...variant,
      isDefault: variant.id === defaultVariantId,
      isActive: activeVariantId
        ? variant.id === activeVariantId
        : variant.id === defaultVariantId,
    } as ReploVariantWithState;
  });
}

export function hasVariantWithConditionField(
  variants: ReploState[],
  conditionField: ConditionField,
) {
  return variants.some((variant) => {
    return (variant.query?.statements ?? []).some(
      (statement) => statement.field === conditionField,
    );
  });
}

const predefinedVariantTypeToData: Record<
  PredefinedVariantType,
  { name: string; field: ConditionField }
> = {
  hover: {
    name: "Hover",
    field: "interaction.hover",
  },
  loading: {
    name: "Loading",
    field: "state.action.loading",
  },
  outOfStock: {
    name: "Out of Stock",
    field: "state.product.selectedVariantUnavailable",
  },
  activeTab: {
    name: "Active Tab",
    field: "state.tabsV2Block.isCurrentTab",
  },
  selectedOption: {
    name: "Selected Option",
    field: "state.product.selectedOptionValues",
  },
  selectedVariant: {
    name: "Selected Variant",
    field: "state.product.selectedVariant",
  },
  selectedSellingPlan: {
    name: "Selected Selling Plan",
    field: "state.product.selectedSellingPlan",
  },
  collapsibleOpen: {
    name: "Collapsible Open",
    field: "state.collapsibleV2.isOpen",
  },
  beforeAfterDragging: {
    name: "Slider is Dragging",
    field: "state.beforeAfterSlider.isDragging",
  },
  tooltipOpen: {
    name: "Tooltip Open",
    field: "state.tooltip.isOpen",
  },
  selectedListItem: {
    name: "Selected List Item",
    field: "state.selectionList.isItemSelected",
  },
  firstItemIsActive: {
    name: "First Item",
    field: "state.group.isFirstItemActive",
  },
  lastItemIsActive: {
    name: "Last Item",
    field: "state.group.isLastItemActive",
  },
  currentItemIsActive: {
    name: "Current Item",
    field: "state.group.isCurrentItemActive",
  },
};

export const buildPredefinedVariant = (
  predefinedVariantType: PredefinedVariantType,
): PredefinedVariant => {
  const data = predefinedVariantTypeToData[predefinedVariantType];
  return {
    name: data.name,
    query: {
      type: "expression",
      operator: "or",
      statements: [
        {
          id: uuidv4(),
          field: data.field,
        },
      ],
    },
    predefinedVariantType,
  };
};
